import { cn } from "@/lib/utils";
import { useId } from "react";

type Size = "small" | "medium" | "large";

/*
  Three sizes, because the coin only ever appears in three places: inline in a
  sentence, in a chip or table cell, and beside a balance headline. Anything
  else is a one-off and belongs in `className`.
*/
const SIZES: Record<Size, string> = {
  small: "size-3",
  medium: "size-3.5",
  large: "size-5",
};

/**
 * The credit coin.
 *
 * A minted disc rather than a flat dot: a shallow gradient from the primary
 * button blue, a rim catching light from the top-left, one soft specular, and
 * a shadow barely darker than the surface it sits on. All of it deliberately
 * faint — the coin appears inline in sentences and inside buttons, where
 * anything glossier reads as an emoji.
 *
 * Every dimension below is relative to the 16-unit viewBox, so `size` (or a
 * `className`) is the only thing that decides how big it lands.
 */
export const CreditIcon = ({
  size = "medium",
  className,
}: {
  size?: Size;
  className?: string;
}) => {
  // Two instances on one screen is the norm (a table of costs, a sentence and
  // a chip), and `url(#…)` resolves to the first match in the document — so the
  // ids have to be per-instance or every coin would paint from the same defs.
  const id = useId();
  const face = `${id}-face`;
  const rim = `${id}-rim`;
  const shine = `${id}-shine`;

  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden
      className={cn(
        "inline-block shrink-0 [filter:drop-shadow(0_0.5px_1px_rgb(0_0_0/0.25))]",
        SIZES[size],
        className,
      )}
    >
      <defs>
        <linearGradient
          id={face}
          x1="3"
          y1="1.5"
          x2="13"
          y2="14.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="var(--color-primary-active)" />
          <stop offset="0.55" stopColor="var(--color-primary)" />
          <stop offset="1" stopColor="var(--color-primary)" />
        </linearGradient>
        {/* Rim light: bright where the face catches it, gone by the bottom. */}
        <linearGradient
          id={rim}
          x1="4"
          y1="1"
          x2="12"
          y2="15"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="rgb(255 255 255 / 0.3)" />
          <stop offset="0.5" stopColor="rgb(255 255 255 / 0.06)" />
          <stop offset="1" stopColor="rgb(255 255 255 / 0)" />
        </linearGradient>
        <linearGradient
          id={shine}
          x1="5"
          y1="2.5"
          x2="8"
          y2="7"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="rgb(255 255 255 / 0.2)" />
          <stop offset="1" stopColor="rgb(255 255 255 / 0)" />
        </linearGradient>
      </defs>

      <circle cx="8" cy="8" r="7" fill={`url(#${face})`} />
      <circle
        cx="8"
        cy="8"
        r="6.6"
        fill="none"
        stroke={`url(#${rim})`}
        strokeWidth="0.7"
      />
      {/* Specular, tilted off the rim rather than centred on it. */}
      <ellipse
        cx="6.1"
        cy="5"
        rx="3.1"
        ry="2"
        fill={`url(#${shine})`}
        transform="rotate(-32 6.1 5)"
      />
    </svg>
  );
};
